"use strict";
// Copy for the goal-delete confirmation, shared by web and mobile.
//
// The sentence was written for mobile in BEN-141 item 5 and lived only there.
// Web's dialog said "…and its contributions will be removed. This can't be
// undone." — the MECHANISM, not the CONSEQUENCE: deleting a goal cascades its
// Contribution rows, and contributions feed the savings actual through
// `untwinned_contributions`, so reported savings for the months those
// contributions fall in genuinely drop. Web could not say that, because its
// goals adapter never mapped `contribution_count` / `contribution_total`
// (BEN-141 N7, #1046). Now both platforms map the fields and build the sentence
// from one function, so the two dialogs cannot drift into two warnings.
//
// The amount is passed already formatted: each platform honours the user's
// `currency_display` preference through its own formatter, and this module has
// no access to either store.
Object.defineProperty(exports, "__esModule", { value: true });
exports.goalDeleteConfirmMessage = goalDeleteConfirmMessage;
exports.goalDeleteConfirmTitle = goalDeleteConfirmTitle;
exports.goalDeleteBucketLine = goalDeleteBucketLine;
exports.goalDeleteConfirmBody = goalDeleteConfirmBody;
/**
 * The body of the delete-confirmation dialog.
 *
 * `contributionTotal` must come from the goal's contribution ledger, NEVER from
 * `currentAmount`: that figure is user-entered and seeded at creation, so it
 * overstates the ledger by whatever was typed into "Saved So Far". On the live
 * demo data "Lexus Rx350" has a $10,000 current amount and zero contributions —
 * a currentAmount-based warning would have claimed $10,000 was about to be
 * destroyed when deleting it costs nothing.
 *
 * The 0 / 1 / many boundaries are where copy like this goes wrong ("1
 * contributions", "$0"), so they are pinned by tests on both platforms.
 */
function goalDeleteConfirmMessage(contributionCount, formattedTotal) {
    const undone = "This can't be undone.";
    // No ledger => nothing to warn about. Must not read "0 logged contributions
    // totalling $0" — 3 of the 6 demo goals are in this state.
    if (contributionCount <= 0)
        return undone;
    const noun = contributionCount === 1 ? "contribution" : "contributions";
    const period = contributionCount === 1
        ? "the month it falls in"
        : "the months they fall in";
    return (`This also deletes ${contributionCount} logged ${noun} totalling ${formattedTotal}. ` +
        `Your reported savings for ${period} will drop by that amount. ${undone}`);
}
/** Title for the same dialog. Separate so the goal name can't be lost in edits. */
function goalDeleteConfirmTitle(goalName) {
    return `Delete "${goalName}"?`;
}
/**
 * What deleting a goal does to its Forecast bucket (BEN-141 FP-022, Paul
 * 2026-10-06, option (a)). The server removes a bucket with its goal only when the
 * goal created it, nothing else uses it, and it holds 0%, and reports that
 * beforehand as `bucket_will_be_removed` from the same function the delete runs.
 * Otherwise the bucket stays, and the confirm says so: an empty "Hawaii" bucket
 * used to outlive "Hawaii" with nothing said.
 *
 * Null when the goal has no bucket. An absent `bucketWillBeRemoved` (a server that
 * predates it) reads as "stays", which is what such a server does.
 */
function goalDeleteBucketLine(bucketName, bucketWillBeRemoved) {
    if (!bucketName)
        return null;
    return bucketWillBeRemoved
        ? `Its Forecast bucket "${bucketName}" is removed too; it was made for this goal and holds no share.`
        : `Its Forecast bucket "${bucketName}" stays; you can remove it on Forecast.`;
}
/**
 * The whole confirm body, so both clients put the bucket line in the same place:
 * first, ahead of the contributions sentence and "This can't be undone."
 */
function goalDeleteConfirmBody(contributionCount, formattedTotal, bucketName, bucketWillBeRemoved) {
    const bucket = goalDeleteBucketLine(bucketName, bucketWillBeRemoved);
    const rest = goalDeleteConfirmMessage(contributionCount, formattedTotal);
    return bucket ? `${bucket} ${rest}` : rest;
}
//# sourceMappingURL=goalCopy.js.map